import React, { useContext, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useFormContext } from 'react-hook-form';
import cn from 'classnames';
import { PapiAttachmentFile, PapiDeviation } from '@wix/da-papi-types';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import TrashCan from '@wix/da-ds/pkg/Icons/24x24/Trashcan';
import Flex from '@wix/da-ds/pkg/Flex';
import GhostButton from '@wix/da-ds/pkg/Buttons/GhostButton';
import Loader from '@wix/da-ds/pkg/Loader';
import { SubmitFormContext } from '../../../context';
import useAttachmentManager from '../../../hooks/useAttachmentManager';
import { useFilePreviewMainUploads } from '../../../hooks/useFilePreviewMainUploads';
import SortableAttachmentThumbSlider from '../SortableAttachmentThumbSlider';
import ImagePreview from '../ImagePreview';
import AttachmentPreview from '../AttachmentPreview';
import AttachmentCaptionEditor from '../AttachmentCaptionEditor';
import MainThumb from '../MainThumb';
import DropZoneIndicator from '../DropZoneIndicator';
import { useSubmitFormStatus } from '../../../hooks/useSubmitFormStatus';
import DropZoneProgressBar from '../DropZoneProgressBar';
import { updatePcpAttachments, removePcpAttachment } from '../../../redux/actions';
import MultiAttachmentPreviewHeader from '../MultiAttachmentPreviewHeader';
import ReplacePreviewImageButton from '../ReplacePreviewImageButton';
import ReplaceSourceFileButton from '../ReplaceSourceFileButton';
import AttachmentCounter from '../AttachmentCounter';

import s from './MultiFilePreview.scss';

interface Props {
  className?: string;
}

const MultiFilePreview: React.FC<Props> = ({ className }) => {
  const { t } = useTranslation();
  const { initResponse, refreshInitData } = useContext(SubmitFormContext) ?? {};
  const { isFormBusy, isMainUploading } = useSubmitFormStatus();
  const dispatch = useDispatch();
  const { setValue } = useFormContext();
  const deviation = initResponse?.deviation;

  const handleAttachSuccess = useCallback(() => {
    refreshInitData?.();
  }, [refreshInitData]);

  const handleReplaceOriginalSuccess = useCallback(() => {
    setValue('addWatermark', false, { shouldDirty: true });
  }, [setValue]);

  const {
    needsPreview,
    isFilm,
    hasError,
    input: previewInput,
    inputOrig,
    showOrigFilePicker,
    getDropzoneRootProps,
    isDragging: isPreviewDropDragging,
    activeUploads: activePreviewUploads,
    activeOrigUploads,
  } = useFilePreviewMainUploads({ onReplaceOriginalSuccess: handleReplaceOriginalSuccess });

  const {
    input: attachmentInput,
    activeUploads: attachmentActiveUploads,
    attachments,
    maxAttachmentsAllowed,
    removeAttachment,
    getDropzoneRootProps: getAttachmentDropzoneRootProps,
    isDragging: isAttachmentDragging,
    isDragAccept,
    isDragReject,
    selectedAttachmentId,
    selectAttachmentId,
  } = useAttachmentManager({
    deviation,
    onSuccess: handleAttachSuccess,
  });

  const attachmentsCount = (attachments || []).length;
  const selectedAttachment: PapiAttachmentFile | undefined = attachments?.find(
    a => a.fileId === selectedAttachmentId
  );

  const handleSliderItemClick = useCallback(
    (attachment: PapiAttachmentFile) => selectAttachmentId(attachment.fileId),
    [selectAttachmentId]
  );

  const handleAttachmentAdded = useCallback(
    (_, updatedDeviation: PapiDeviation) => {
      const additionalMedia = updatedDeviation.extended?.additionalMedia ?? [];
      dispatch(
        updatePcpAttachments(
          additionalMedia.map(media => ({
            ...media,
            isOriginal: true,
          }))
        )
      );
    },
    [dispatch]
  );

  const handleAttachmentRemove = useCallback(
    (fileId: number) => {
      removeAttachment(fileId);
      dispatch(removePcpAttachment(fileId));
    },
    [dispatch, removeAttachment]
  );

  const replaceUploads = [...activePreviewUploads, ...activeOrigUploads];
  const activeUpload = [...attachmentActiveUploads, ...replaceUploads].pop();
  const uploadProgress = activeUpload?.progress;
  const allowUpload = !isFormBusy && !isMainUploading;

  const isPublished = deviation?.isPublished;
  const showFakeFilmThumb = isFilm && !isPublished;

  const showUploadOverlay =
    isMainUploading || attachmentActiveUploads.length > 0 || replaceUploads.length > 0;

  const mainSlotView = !selectedAttachment;
  const attachmentView = !!selectedAttachment;

  const renderMainFileSlot = () => (
    <div className={s['preview']}>
      {needsPreview && allowUpload && (
        <div
          className={cn(
            s['need-preview-root'],
            isPreviewDropDragging && s['drag-active'],
            hasError && s['with-error']
          )}
          {...(allowUpload ? getDropzoneRootProps() : {})}
        >
          {previewInput}
          <Loader
            className={s['llama']}
            size="big"
            state={showFakeFilmThumb ? 'popcorn' : 'static'}
          />
          <span className={s['title']}>{t('deviationSubmit.form.preview.file.title')}</span>
          <span className={cn(s['text'], hasError && s['has-error'])}>
            {t('deviationSubmit.form.preview.file.text')}
          </span>
          <GhostButton className={s['add-btn']} variant="main" size="large">
            {t('deviationSubmit.form.preview.file.btn')}
          </GhostButton>
        </div>
      )}
      {allowUpload && !needsPreview && (
        <ImagePreview className={s['preview']} deviation={deviation} />
      )}
    </div>
  );

  const renderPreviewContent = () => (
    <div className={s['preview']}>
      {mainSlotView && renderMainFileSlot()}
      {selectedAttachment && (
        <AttachmentPreview
          key={selectedAttachment.fileId}
          className={s['preview']}
          attachment={selectedAttachment}
        />
      )}
    </div>
  );

  return (
    <>
      <Flex direction="column" className={cn(s['root'], className)}>
        {mainSlotView && (
          <MultiAttachmentPreviewHeader
            className={s['header']}
            leading={
              <AttachmentCounter
                attachmentsCount={attachmentsCount}
                maxAttachmentsAllowed={maxAttachmentsAllowed}
              />
            }
            trailing={
              <Flex inline direction="row" alignItems="center" gap={8}>
                <ReplaceSourceFileButton
                  inputOrig={inputOrig}
                  allowUpload={allowUpload}
                  showOrigFilePicker={showOrigFilePicker}
                />
                <ReplacePreviewImageButton
                  previewFileInput={allowUpload && !needsPreview ? previewInput : null}
                  allowUpload={allowUpload}
                  needsPreview={!!needsPreview}
                  getDropzoneRootProps={getDropzoneRootProps}
                />
              </Flex>
            }
          />
        )}
        {attachmentView && (
          <MultiAttachmentPreviewHeader
            className={s['header']}
            leading={
              <AttachmentCounter
                attachmentsCount={attachmentsCount}
                maxAttachmentsAllowed={maxAttachmentsAllowed}
              />
            }
            trailing={
              <TextButton
                variant="main"
                size="medium"
                icon={TrashCan}
                disabled={!allowUpload}
                onClick={() => handleAttachmentRemove(selectedAttachmentId ?? 0)}
              >
                {t('deviationSubmit.form.removeAttachment.button.text')}
              </TextButton>
            }
          />
        )}

        <div className={s['content']} {...(allowUpload ? getAttachmentDropzoneRootProps() : {})}>
          <Flex alignItems="center" justifyContent="center" fullHeight>
            {showUploadOverlay && (
              <div className={s['uploading-wrapper']}>
                <DropZoneProgressBar
                  className={s['uploading']}
                  label={t('uploadZone.progress.title')}
                  progress={uploadProgress}
                />
              </div>
            )}
            {allowUpload ? (
              <DropZoneIndicator
                className={s['dropzone']}
                isDragActive={isAttachmentDragging}
                isDragAccept={isDragAccept}
                isDragReject={isDragReject}
              >
                {renderPreviewContent()}
              </DropZoneIndicator>
            ) : (
              renderPreviewContent()
            )}
            {attachmentInput}
          </Flex>
        </div>

        {deviation && attachmentsCount > 0 && (
          <div className={s['caption-input-wrapper']}>
            <AttachmentCaptionEditor
              key={selectedAttachmentId}
              deviation={deviation}
              attachment={selectedAttachment}
              placeholder={t('deviationSubmit.form.imageReplace.caption.label')}
            />
          </div>
        )}

        <div className={s['footer']}>
          <Flex direction="row">
            {deviation && (
              <MainThumb
                className={s['main-thumb']}
                item={deviation}
                onClick={() => selectAttachmentId(undefined)}
                selected={!selectedAttachment}
              />
            )}
            <div className={s['separator']} />
            <SortableAttachmentThumbSlider
              className={s['thumbs-slider']}
              deviation={deviation}
              selectedItemId={selectedAttachmentId}
              onItemClick={handleSliderItemClick}
              isUploading={isMainUploading || attachmentActiveUploads.length > 0}
              removeAttachment={handleAttachmentRemove}
              onAttachmentAdded={handleAttachmentAdded}
            />
          </Flex>
        </div>
      </Flex>
    </>
  );
};
MultiFilePreview.displayName = 'MultiFilePreview';

export default MultiFilePreview;
